import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Target,
  TrendingUp,
  Briefcase,
  CheckCircle2,
  AlertTriangle,
  Plus,
  Eye,
  Edit,
  MapPin,
  Users,
  Award,
  BarChart3,
  Calendar,
  Clock,
  Star,
  BookOpen,
  FileText,
  Building,
  User,
  ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DCFEmployeeTab = ({ employeeId, employeeName, cargoId, departamentoId }) => {
  const [loading, setLoading] = useState(true);
  const [dcfData, setDcfData] = useState(null);
  const [competenciasAnalysis, setCompetenciasAnalysis] = useState(null);
  const [careerPath, setCareerPath] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);

  useEffect(() => {
    if (employeeId) {
      loadDCFData();
    }
  }, [employeeId, cargoId]);

  const loadDCFData = async () => {
    try {
      setLoading(true);
      
      // Carregamento paralelo de dados
      const [dcfResponse, competenciasResponse, careerResponse, performanceResponse] = await Promise.allSettled([
        axios.get(`${API}/api/dcf/employee/${employeeId}`),
        axios.get(`${API}/api/dcf/employee/${employeeId}/competencias-analysis`),
        axios.get(`${API}/api/dcf/employee/${employeeId}/career-path`),
        axios.get(`${API}/api/dcf/employee/${employeeId}/performance-alignment`)
      ]);

      // Processar respostas
      if (dcfResponse.status === 'fulfilled') {
        setDcfData(dcfResponse.value.data);
      }
      
      if (competenciasResponse.status === 'fulfilled') {
        setCompetenciasAnalysis(competenciasResponse.value.data);
      }
      
      if (careerResponse.status === 'fulfilled') {
        setCareerPath(careerResponse.value.data);
      }
      
      if (performanceResponse.status === 'fulfilled') {
        setPerformanceData(performanceResponse.value.data);
      }

    } catch (error) {
      console.error('Erro ao carregar dados DCF:', error);
      toast.error('Erro ao carregar informações DCF');
    } finally {
      setLoading(false);
    }
  };

  const renderDCFOverview = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Briefcase className="h-5 w-5 text-blue-600" />
          <span>Vinculação DCF</span>
        </CardTitle>
        <CardDescription>
          Informações da DCF vinculada ao colaborador
        </CardDescription>
      </CardHeader>
      <CardContent>
        {dcfData && dcfData.has_dcf ? (
          <div className="space-y-6">
            {/* Vinculação Card - Estilo similar ao print */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                {/* Lado Esquerdo - Colaborador */}
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-8 w-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{employeeName || dcfData.funcionario_nome}</h3>
                    <p className="text-gray-600">{dcfData.cargo_nome || 'Cargo não definido'}</p>
                  </div>
                </div>

                {/* Seta de transição */}
                <div className="mx-6">
                  <ArrowRight className="h-8 w-8 text-gray-400" />
                </div>

                {/* Lado Direito - DCF */}
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <Briefcase className="h-8 w-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{dcfData.nome_cargo}</h3>
                    <p className="text-gray-600">Nível: {dcfData.nivel_hierarquico || 'Não definido'}</p>
                  </div>
                </div>
              </div>

              {/* Informações adicionais da vinculação */}
              <div className="mt-6 pt-6 border-t border-blue-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Data de Vinculação */}
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Vinculado em</p>
                      <p className="text-sm font-medium text-gray-700">
                        {dcfData.vinculacao_data 
                          ? new Date(dcfData.vinculacao_data).toLocaleDateString('pt-BR')
                          : 'Data não disponível'}
                      </p>
                    </div>
                  </div>

                  {/* Match Score */}
                  {dcfData.match_score && (
                    <div className="flex items-center space-x-2">
                      <Star className="h-5 w-5 text-yellow-600" />
                      <div>
                        <p className="text-xs text-gray-500">Match Score</p>
                        <p className="text-sm font-medium text-gray-700">
                          {dcfData.match_score}%
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Departamento */}
                  {dcfData.departamento_nome && (
                    <div className="flex items-center space-x-2">
                      <Building className="h-5 w-5 text-purple-600" />
                      <div>
                        <p className="text-xs text-gray-500">Departamento</p>
                        <p className="text-sm font-medium text-gray-700">
                          {dcfData.departamento_nome}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Observações */}
                {dcfData.observacoes_vinculacao && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
                    <p className="text-xs text-gray-500 mb-1">Observações</p>
                    <p className="text-sm text-gray-700">{dcfData.observacoes_vinculacao}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Ações */}
            <div className="flex space-x-3 pt-4 border-t">
              <Button size="sm" className="flex items-center space-x-2">
                <Eye className="h-4 w-4" />
                <span>Ver DCF Completa</span>
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Sem DCF Vinculada</h3>
            <p className="text-gray-600 mb-4">
              Este colaborador ainda não possui uma DCF (Descrição de Cargo e Função) vinculada.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Acesse o módulo DCF → Vinculações para criar uma vinculação para este colaborador.
            </p>
            <Button className="flex items-center space-x-2 mx-auto" onClick={() => window.location.href = '/dcf/vinculacoes'}>
              <Plus className="h-4 w-4" />
              <span>Ir para Vinculações DCF</span>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderCompetenciasAnalysis = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Award className="h-5 w-5 text-purple-600" />
          <span>Análise de Competências</span>
        </CardTitle>
        <CardDescription>
          Competências necessárias vs competências atuais do funcionário
        </CardDescription>
      </CardHeader>
      <CardContent>
        {competenciasAnalysis ? (
          <div className="space-y-6">
            {/* Resumo */}
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-700">
                  {competenciasAnalysis.competencias_atendidas || 0}
                </div>
                <div className="text-sm text-green-600">Competências Atendidas</div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-700">
                  {competenciasAnalysis.competencias_parciais || 0}
                </div>
                <div className="text-sm text-yellow-600">Parcialmente Atendidas</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-700">
                  {competenciasAnalysis.competencias_gap || 0}
                </div>
                <div className="text-sm text-red-600">Gap de Competências</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-700">
                  {competenciasAnalysis.score_geral || 0}%
                </div>
                <div className="text-sm text-blue-600">Score Geral</div>
              </div>
            </div>

            {/* Lista de Competências */}
            <div className="space-y-3">
              <h4 className="font-semibold">Competências Detalhadas</h4>
              {competenciasAnalysis.detalhes?.map((comp, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h5 className="font-medium">{comp.nome}</h5>
                    <p className="text-sm text-gray-600">{comp.categoria}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-center">
                      <div className="text-sm font-medium">Necessário</div>
                      <div className="text-xs text-gray-600">{comp.nivel_necessario}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium">Atual</div>
                      <div className="text-xs text-gray-600">{comp.nivel_atual || 'Não avaliado'}</div>
                    </div>
                    <Badge variant={
                      comp.status === 'atendida' ? 'default' : 
                      comp.status === 'parcial' ? 'secondary' : 'destructive'
                    }>
                      {comp.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Análise de competências não disponível</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderCareerPath = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <TrendingUp className="h-5 w-5 text-green-600" />
          <span>Plano de Carreira</span>
        </CardTitle>
        <CardDescription>
          Possibilidades de progressão baseadas na DCF e competências
        </CardDescription>
      </CardHeader>
      <CardContent>
        {careerPath ? (
          <div className="space-y-6">
            {/* Próximas Posições */}
            <div className="space-y-3">
              <h4 className="font-semibold">Próximas Posições Possíveis</h4>
              {careerPath.proximas_posicoes?.map((posicao, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium">{posicao.cargo}</h5>
                      <p className="text-sm text-gray-600">{posicao.departamento}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant={posicao.probabilidade >= 80 ? 'default' : 'secondary'}>
                        {posicao.probabilidade}% de compatibilidade
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        Tempo estimado: {posicao.tempo_estimado}
                      </p>
                    </div>
                  </div>
                  
                  {posicao.competencias_necessarias && (
                    <div className="mt-3">
                      <p className="text-sm font-medium mb-2">Competências a desenvolver:</p>
                      <div className="flex flex-wrap gap-2">
                        {posicao.competencias_necessarias.map((comp, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {comp}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Plano de Desenvolvimento */}
            {careerPath.plano_desenvolvimento && (
              <div className="space-y-3">
                <h4 className="font-semibold">Plano de Desenvolvimento Sugerido</h4>
                <div className="space-y-2">
                  {careerPath.plano_desenvolvimento.map((acao, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <Clock className="h-4 w-4 text-blue-600 mt-1" />
                      <div className="flex-1">
                        <h6 className="font-medium">{acao.acao}</h6>
                        <p className="text-sm text-gray-600">{acao.descricao}</p>
                        <p className="text-xs text-blue-600 mt-1">
                          Prazo: {acao.prazo} | Prioridade: {acao.prioridade}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Plano de carreira não disponível</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <div className="animate-pulse space-y-2">
                <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="animate-pulse space-y-4">
                <div className="grid md:grid-cols-4 gap-4">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="h-20 bg-gray-200 rounded"></div>
                  ))}
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {renderDCFOverview()}
    </div>
  );
};

export default DCFEmployeeTab;